import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { companyTodayDate } from "@/lib/time";

/**
 * Brand-styled calendar popup — a custom component, no third-party library.
 * Picking a day (and a time, when `withTime`) updates a draft inside the popup;
 * the value is only saved when the user presses **Done** (or Enter). Cancel /
 * Esc / clicking outside discard the draft. `onChange` receives a plain string:
 * `"YYYY-MM-DD"`, or `"YYYY-MM-DDTHH:mm"` when `withTime`, or `""` when cleared.
 *
 * The popup is portalled to `<body>` and positioned `fixed` over everything:
 * inside a form it would sit in a `<label>` (a click on Done could be forwarded
 * back to the field and re-open it), under the sticky action bar of the
 * quick-create page (Done unreachable on a phone) or clipped by a scrolling
 * container. Done always saves and closes; Cancel closes without a change.
 */
export interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  withTime?: boolean;
  min?: string; // "YYYY-MM-DD", inclusive
  max?: string; // "YYYY-MM-DD", inclusive
  placeholder?: string;
  className?: string;
  id?: string;
}

const TRIGGER_CLS =
  "flex w-full items-center gap-2 rounded-md border border-olive-600 bg-olive-900 " +
  "px-3 py-2 text-left text-sm text-sand focus:border-gold focus:outline-none";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameDay = (a: Date, b: Date) => ymd(a) === ymd(b);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

function parseDateOnly(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}
function parseTime(s: string): { h: number; m: number } {
  const m = /T(\d{2}):(\d{2})/.exec(s);
  return m ? { h: Number(m[1]), m: Number(m[2]) } : { h: 9, m: 0 };
}

/** Monday-first 6×7 grid of dates covering `viewMonth`. */
function monthGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  const offset = (first.getDay() + 6) % 7; // 0 = Monday
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export function DatePicker({
  value,
  onChange,
  withTime = false,
  min,
  max,
  placeholder,
  className,
  id,
}: DatePickerProps) {
  const { t } = useTranslation();
  const lang = i18n.language || "en";
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties>({ visibility: "hidden" });

  const selected = useMemo(() => parseDateOnly(value), [value]);
  const selTime = useMemo(() => parseTime(value), [value]);

  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(selected ?? companyTodayDate()),
  );
  const [draft, setDraft] = useState<Date | null>(selected);
  const [h, setH] = useState(selTime.h);
  const [m, setM] = useState(selTime.m);

  const minD = min ? parseDateOnly(min) : null;
  const maxD = max ? parseDateOnly(max) : null;
  const disabled = (d: Date) =>
    (minD != null && ymd(d) < ymd(minD)) || (maxD != null && ymd(d) > ymd(maxD));

  // (Re)sync the draft to the saved value whenever the popup opens.
  useEffect(() => {
    if (!open) return;
    setDraft(selected);
    setH(selTime.h);
    setM(selTime.m);
    setViewMonth(startOfMonth(selected ?? companyTodayDate()));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fixed position next to the field: below it when it fits, else above, always
  // kept inside the viewport; follows the field on scroll / resize.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const field = wrapRef.current?.getBoundingClientRect();
      const pop = popRef.current;
      if (!field || !pop) return;
      const gap = 4;
      const margin = 8;
      const w = pop.offsetWidth;
      const h = pop.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top = field.bottom + gap;
      if (top + h > vh - margin && field.top - gap - h >= margin) top = field.top - gap - h;
      top = Math.max(margin, Math.min(top, vh - h - margin));
      const left = Math.max(margin, Math.min(field.left, vw - w - margin));
      setPos({ top, left });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, withTime]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const close = () => {
    setOpen(false);
    setPos({ visibility: "hidden" });
  };
  const commit = () => {
    if (!draft) return;
    onChange(withTime ? `${ymd(draft)}T${pad(h)}:${pad(m)}` : ymd(draft));
    close();
  };
  const clear = () => {
    onChange("");
    close();
  };

  const display = useMemo(() => {
    if (!selected) return "";
    const d = selected.toLocaleDateString(lang, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return withTime ? `${d}, ${pad(selTime.h)}:${pad(selTime.m)}` : d;
  }, [selected, selTime, withTime, lang]);

  const weekdays = useMemo(() => {
    const base = new Date(2024, 0, 1); // a Monday
    return Array.from({ length: 7 }, (_, i) =>
      addDays(base, i).toLocaleDateString(lang, { weekday: "short" }),
    );
  }, [lang]);

  const onGridKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return close();
    if (e.key === "Enter") return commit();
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (e.key in step) {
      e.preventDefault();
      const next = addDays(draft ?? companyTodayDate(), step[e.key]);
      if (!disabled(next)) {
        setDraft(next);
        setViewMonth(startOfMonth(next));
      }
    }
  };

  return (
    <div ref={wrapRef} className={"relative " + (className ?? "")}>
      <button
        type="button"
        id={id}
        className={TRIGGER_CLS}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span className={display ? "" : "text-[rgb(var(--nm-placeholder))]"}>
          {display || placeholder || t("datepicker.placeholder")}
        </span>
        <svg
          className="ml-auto h-4 w-4 text-sand-muted"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6 2a1 1 0 0 1 1 1v1h6V3a1 1 0 1 1 2 0v1h1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h1V3a1 1 0 0 1 1-1Zm11 6H3v8h14V8Z" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={popRef}
          role="dialog"
          aria-label={t("datepicker.title")}
          onKeyDown={onGridKey}
          // React events bubble through a portal: keep them from reaching a
          // parent's click handler (a row, a card) as if the field was clicked
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          tabIndex={-1}
          style={pos}
          className="fixed z-50 w-[17rem] rounded-lg border border-olive-600 bg-olive-800 p-3 shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sand-muted hover:bg-olive-700 hover:text-sand"
              onClick={() => setViewMonth((v) => addMonths(v, -1))}
              aria-label={t("datepicker.prevMonth")}
            >
              ‹
            </button>
            <div className="text-sm font-medium text-sand">
              {viewMonth.toLocaleDateString(lang, {
                month: "long",
                year: "numeric",
              })}
            </div>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-sand-muted hover:bg-olive-700 hover:text-sand"
              onClick={() => setViewMonth((v) => addMonths(v, 1))}
              aria-label={t("datepicker.nextMonth")}
            >
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-xs text-sand-muted">
            {weekdays.map((w) => (
              <div key={w} className="py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {monthGrid(viewMonth).map((d) => {
              const other = d.getMonth() !== viewMonth.getMonth();
              const isSel = draft != null && sameDay(d, draft);
              const isToday = sameDay(d, companyTodayDate());
              const off = disabled(d);
              return (
                <button
                  key={ymd(d)}
                  type="button"
                  disabled={off}
                  onClick={() => setDraft(d)}
                  className={
                    "h-8 rounded-md text-sm transition " +
                    (isSel
                      ? "bg-gold font-semibold text-olive-900 "
                      : off
                        ? "cursor-not-allowed text-sand-muted opacity-30 "
                        : "hover:bg-olive-700 ") +
                    (other && !isSel ? "text-sand-muted " : "") +
                    (isToday && !isSel ? "ring-1 ring-gold/60 " : "")
                  }
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {withTime && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="text-xs text-sand-muted">
                {t("datepicker.time")}
              </span>
              <select
                className="rounded-md border border-olive-600 bg-olive-900 px-2 py-1 text-sand"
                value={h}
                onChange={(e) => setH(Number(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {pad(i)}
                  </option>
                ))}
              </select>
              <span>:</span>
              <select
                className="rounded-md border border-olive-600 bg-olive-900 px-2 py-1 text-sand"
                value={m}
                onChange={(e) => setM(Number(e.target.value))}
              >
                {/* every minute: a pickup at 14:37 (a flight) must survive the
                    form unchanged — a 5-minute list showed «00» for it and lost
                    the minutes on the next edit */}
                {Array.from({ length: 60 }, (_, i) => i).map((mm) => (
                  <option key={mm} value={mm}>
                    {pad(mm)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              className="text-xs text-sand-muted hover:text-sand"
              onClick={clear}
            >
              {t("common.clear")}
            </button>
            <button
              type="button"
              className="ml-auto text-xs text-sand-muted hover:text-sand"
              onClick={close}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              disabled={!draft}
              className="rounded-md bg-gold px-3 py-1 text-xs font-semibold text-olive-900 disabled:opacity-40"
              onClick={commit}
            >
              {t("common.done")}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/** Date-only picker. `onChange` gets `"YYYY-MM-DD"` (or `""`). */
export function DateField(props: Omit<DatePickerProps, "withTime">) {
  return <DatePicker {...props} />;
}

/** Date + time picker. `onChange` gets `"YYYY-MM-DDTHH:mm"` (or `""`). */
export function DateTimeField(props: Omit<DatePickerProps, "withTime">) {
  return <DatePicker {...props} withTime />;
}
